
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Linkedin, Twitter, Github, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import {FaWhatsapp, FaTiktok} from 'react-icons/fa';
const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Accueil' },
    { to: '/about', label: 'À propos' },
    { to: '/team', label: 'Équipe' },
    { to: '/projects', label: 'Projets' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-cyan-50">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src="/images/LogoKY2.png" 
                alt="Kynovia Tech Logo"
                className="h-12 w-auto transform transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium transition-colors relative group ${
                    location.pathname === link.to
                      ? 'text-cyan-600'
                      : 'text-gray-700 hover:text-cyan-600'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 w-full h-0.5 bg-cyan-600 transform transition-transform ${
                      location.pathname === link.to ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              ))}
              <Link
                to="/contact"
                className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-105 transition-all"
              >
                Demander un devis
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-4 py-2 rounded-lg font-medium transition-colors ${
                      location.pathname === link.to
                        ? 'bg-cyan-50 text-cyan-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  className="block px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium text-center"
                >
                  Demander un devis
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Main Content */}
      <main className="flex-grow pt-20">{children}</main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-4">
              <img 
                src="/images/LogoKY1.png" 
                alt="Kynovia Tech Logo"
                className="h-16 w-auto"
              />
              <p className="text-gray-400 text-sm">
                Transformation digitale, Data et Développement logiciel.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Liens rapides</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/legal"
                    className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
                  >
                    Mentions légales
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3 text-sm">
                  <Mail size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <a href="mailto:contact@kynoviatech.com" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    contact@kynoviatech.com
                  </a>
                </li>
                <li className="flex items-start space-x-3 text-sm">
                  <Phone size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <a href="tel:+33668466995" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    +33 6 68 46 69 95
                  </a>
                </li>
                <li className="flex items-start space-x-3 text-sm">
                  <MapPin size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400">12 Rue de la Plaine<br />75000 Paris, France</span>
                </li>
              </ul>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Suivez-nous</h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/kynovia-tec"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://x.com/@Kynoviatech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <Twitter size={20} />
                </a>

                <a
                  href="https://instagram.com/kynovia_tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <Instagram size={20} />
                </a>

                <a
                  href="https://wa.me/33668466995"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <FaWhatsapp size={20} />
                </a>

                <a
                  href="https://tiktok.com/@kynovia_tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <FaTiktok size={20} />
                </a>


                <a
                  href="https://github.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/50 transform hover:scale-110"
                >
                  <Github size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-400">
            <p>© {new Date().getFullYear()}{" "} KynoviaTech. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
